import styled, { createGlobalStyle } from 'styled-components';
import {
  Button,
  Card,
  Typography,
  Row,
  Col,
  Space,
  Divider,
  Badge,
  Modal
} from 'antd';
import { DownloadOutlined, InfoCircleOutlined } from '@ant-design/icons';
import { IToken } from 'store/ducks/globalTypes';

const { Title, Text } = Typography;

export const Container = styled.div<IToken & { scheme?: string }>`
  padding: 8px 24px;
  height: auto;
  background: ${(props) => {
    const { themesList } = require('constants/themesList');
    const currentTheme = themesList[props.scheme || 'deepBlueTheme'];
    return (
      currentTheme?.colorBgLayout || themesList.deepBlueTheme.colorBgLayout
    );
  }};
`;

export const Header = styled.div`
  margin-bottom: 4px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
`;

export const HeaderContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0px;
`;

export const HeaderTitle = styled(Title)`
  margin: 0 !important;
`;

export const HeaderSubtitle = styled(Text)`
  font-size: 16px;
  margin-top: -8px;
  line-height: 1.2;
`;

export const DownloadBadge = styled(Badge)`
  margin-left: 16px;
`;

export const ContentRow = styled(Row)`
  width: 100%;
  margin-top: 0 !important;
  justify-content: center;
`;

export const SidebarCol = styled(Col)`
  display: flex;
  height: 100%;
`;

export const MainCol = styled(Col)`
  display: flex;
  flex-direction: column;
  height: 100%;
  justify-content: center;
  align-items: center;
`;

export const SectionTitle = styled(Title)`
  margin: 0 !important;
`;

export const SectionDivider = styled(Divider)`
  margin: 8px 0;
`;

export const DataSourceList = styled(Space)`
  width: 100%;
`;

export const ReportsTitle = styled(Title)`
  margin: 0 0 24px 0 !important;
  text-align: center;
`;

export const ReportsGrid = styled.div<IToken & { scheme?: string }>`
  display: flex;
  flex-direction: column;
  gap: 40px;
  width: 100%;
  max-width: 350px;
  margin: 0 auto;
  position: relative;

  &::before {
    content: '';
    position: absolute;
    left: -2%;
    top: -10px;
    bottom: -15px;
    width: 2px;
    background: ${({ token }) => token.colorPrimary};
    z-index: 0;
  }
`;

export const ReportCol = styled.div`
  display: flex;
  width: 100%;
  position: relative;
  z-index: 1;

  > * {
    width: 100%;
  }
`;

export const CardTitle = styled(Title)<IToken>`
  margin: 0 !important;
  color: ${({ token }) => token.colorTextLightSolid} !important;
  font-size: 14px !important;
  font-weight: 600 !important;
  line-height: 1.3;
  text-align: center;
`;

export const CardDescription = styled(Text)<IToken>`
  display: block;
  margin-bottom: 16px;
  font-size: 11px;
  color: ${({ token }) => token.colorTextSecondary} !important;
  line-height: 1.3;
  min-height: 28px;
`;

export const SmallText = styled(Text)`
  font-size: 12px;
  line-height: 1.4;
`;

export const DownloadIcon = styled(DownloadOutlined)<IToken>`
  font-size: 16px;
  color: ${({ token }) => token.colorTextSecondary};
`;

export const InfoCard = styled(Card)<IToken>`
  height: 100%;
  border-radius: 12px;
  box-shadow: ${({ token }) => token.boxShadowTertiary};
  background: ${({ token }) => token.colorBgContainer};
  border: none;

  .ant-card-body {
    padding: 16px;
    background: ${({ token }) => token.colorBgContainer};
  }
`;

export const DataItem = styled.div<IToken>`
  display: flex;
  align-items: flex-start;
  gap: 8px;
  padding: 12px;
  border-radius: 8px;
  background: ${({ token }) => token.colorBgLayout};
  border: 1px solid ${({ token }) => token.colorBorder};
  transition: all 0.2s ease;

  &:hover {
    background: ${({ token }) => token.colorFillTertiary};
    transform: translateY(-1px);
  }
`;

export const DataIcon = styled.div<IToken & { iconType?: string }>`
  font-size: 20px;
  min-width: 24px;
  display: flex;
  align-items: center;
  justify-content: center;

  ${({ iconType, token }) => {
    switch (iconType) {
      case 'primary':
        return `color: ${token.colorPrimary};`;
      case 'success':
        return `color: ${token.colorSuccess};`;
      case 'warning':
        return `color: ${token.colorWarning};`;
      case 'danger':
        return `color: ${token.colorError};`;
      default:
        return '';
    }
  }}
`;

export const ReportCard = styled(Card)<IToken & { scheme?: string }>`
  height: auto;
  width: 100%;
  border-radius: 8px;
  background: ${({ token }) => token.colorPrimary};
  border: none;
  box-shadow: ${({ token }) => token.boxShadowSecondary};
  cursor: pointer;
  transition: all 0.3s ease;
  position: relative;

  &:hover {
    transform: translateY(-1px);
    box-shadow: ${({ token }) => token.boxShadow};
  }

  .ant-card-body {
    padding: 12px 16px;
    height: auto;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
  }
`;

export const CardHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
`;

export const IconWrapper = styled.div<{ type: string; token: any }>`
  width: 32px;
  height: 32px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;

  ${({ type, token }) => {
    switch (type) {
      case 'primary':
        return `
          background: ${token.colorPrimaryBg};
          color: ${token.colorPrimary};
        `;
      case 'success':
        return `
          background: ${token.colorSuccessBg};
          color: ${token.colorSuccess};
        `;
      case 'warning':
        return `
          background: ${token.colorWarningBg};
          color: ${token.colorWarning};
        `;
      case 'danger':
        return `
          background: ${token.colorErrorBg};
          color: ${token.colorError};
        `;
      default:
        return `
          background: ${token.colorPrimaryBg};
          color: ${token.colorPrimary};
        `;
    }
  }}
`;

export const DownloadButton = styled(Button)<IToken>`
  margin-top: 8px;
  margin-left: 4px;
  margin-right: 4px;
  border-radius: 6px;
  height: 28px;
  font-size: 11px;
  font-weight: 500;

  &:hover {
    transform: translateY(-1px);
  }
`;

// Legacy styles for backward compatibility
export const ButtonElement = styled(Button)`
  width: 350px;
  align-self: center;
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  height: fit-content;
  margin-bottom: 16px;
`;

export const ReportingRow = styled('div')`
  display: flex;
  justify-content: space-between;
`;

export const ButtonDiv = styled('div')`
  margin: 16px;
`;

export const TextBoxP = styled('p')<IToken>`
  font-size: ${(props) => `${props.token.fontSizeSM}px`};
`;
export const HeaderSection = styled.div<IToken & { scheme?: string }>`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 4px;
  max-width: 80%;
  margin-left: 10.5%;

  h2 {
    margin: 0;
    font-size: 24px;
    color: ${({ token, scheme }) => {
      const darkThemes = ['darkUITheme', 'deepNavyTheme'];
      return darkThemes.includes(scheme || '')
        ? token.colorTextLightSolid
        : token.colorText;
    }};
  }
`;

export const SubHeaderSection = styled.div<IToken & { scheme?: string }>`
  margin-bottom: 16px;
  max-width: 80%;
  margin-left: 10.5%;
  text-align: center;

  p {
    margin: 0;
    color: ${({ token, scheme }) => {
      const darkThemes = ['darkUITheme', 'deepNavyTheme'];
      const { themesList } = require('constants/themesList');
      const currentTheme = themesList[scheme || 'deepBlueTheme'];
      if (darkThemes.includes(scheme || '')) {
        return token.colorTextLightSolid;
      } else if (currentTheme?.colorPrimaryText) {
        return currentTheme.colorPrimaryText;
      } else {
        return token.colorTextSecondary;
      }
    }};
    font-size: 16px;
  }
`;

export const modalStyles = {
  maxWidth: '95vw',
  top: 20
};

export const ReportTableWrapper = styled.div<{ scheme?: string }>`
  .ant-table-wrapper .ant-table .ant-table-thead {
    tr:first-child > th[colspan] {
      padding-top: 5px !important;
    }

    tr:last-child > th {
      padding-top: 5px !important;
    }
  }

  ${({ scheme }) => {
    const { themesList } = require('constants/themesList');
    const currentTheme = themesList[scheme || 'deepBlueTheme'];
    if (currentTheme?.colorPrimaryText) {
      return `
        .ant-table-thead .ant-table-cell,
        .ant-table-thead th,
        .ant-table-thead .ant-table-column-title,
        .ant-table-thead span,
        .ant-table-tbody .ant-table-cell,
        .ant-table-tbody td,
        .ant-table-tbody tr,
        .ant-table .ant-table-cell {
          color: ${currentTheme.colorPrimaryText} !important;
        }
      `;
    }
    return '';
  }}
`;

export const CardTitleWithInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

export const CardInfoIcon = styled.div`
  position: absolute;
  bottom: 8px;
  right: 8px;
  z-index: 10;
`;

export const InfoIcon = styled(InfoCircleOutlined)<IToken>`
  font-size: 14px;
  color: ${({ token }) => token.colorTextLightSolid};
  cursor: pointer;
`;

export const PopoverContent = styled.div`
  max-width: 600px;
  width: 600px;
  line-height: 1.4;
  padding: 4px;
  position: relative;
`;

// Global styles for centered popover
export const CenteredPopoverStyles = `
  .centered-popover {
    position: fixed !important;
    top: 10% !important;
    left: 50% !important;
    transform: translateX(-50%) !important;
    width: 50vw !important;
    max-height: 80vh !important;
    z-index: 1050 !important;
  }
`;

export const GlobalPopoverStyles = createGlobalStyle`
  .centered-popover {
    position: fixed !important;
    top: 10% !important;
    left: 50% !important;
    transform: translateX(-50%) !important;
    width: 50vw !important;
    max-height: 80vh !important;
    z-index: 1050 !important;
  }
`;

export const PopoverCloseButton = styled.button`
  position: absolute;
  top: -20px;
  right: -100px;
  background: none;
  border: none;
  font-size: 18px;
  cursor: pointer;
  color: #666;
  padding: 6px;
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  z-index: 100;

  &:hover {
    background-color: #f0f0f0;
    color: #333;
  }
`;

export const PopoverTitle = styled.div`
  font-weight: bold;
  margin-bottom: 12px;
`;

export const PopoverSection = styled.div`
  margin-bottom: 12px;
`;

export const PopoverFieldsTitle = styled.div`
  font-weight: bold;
  margin-bottom: 8px;
`;

export const PopoverFieldsGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 8px 16px;
  margin-bottom: 12px;
`;

export const PopoverField = styled.div`
  font-size: 13px;

  strong {
    font-weight: bold;
    display: block;
    margin-bottom: 2px;
  }
`;

export const PopoverFooter = styled.div`
  margin-top: 12px;
  font-size: 13px;
`;

export const ReportValidityHeaderWrapper = styled.div`
  display: inline-block;
  position: relative;
  padding-right: 40px;
  white-space: nowrap;
`;

export const StyledModal = styled(Modal)<{ scheme?: string }>`
  .ant-modal-title {
    color: ${({ scheme }) => {
      const { themesList } = require('constants/themesList');
      const currentTheme = themesList[scheme || 'deepBlueTheme'];
      return currentTheme?.colorPrimaryText
        ? `${currentTheme.colorPrimaryText} !important`
        : 'inherit';
    }};
  }
  .ant-modal-close,
  .ant-modal-close-x {
    color: ${({ scheme }) => {
      const { themesList } = require('constants/themesList');
      const currentTheme = themesList[scheme || 'deepBlueTheme'];
      return currentTheme?.colorPrimaryText
        ? `${currentTheme.colorPrimaryText} !important`
        : 'inherit';
    }};
  }
`;

export const ModalHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: calc(100% - 20px);
  padding-right: 0;
`;

export const ExportButtonWrapper = styled.div`
  margin-bottom: 16px;
  display: flex;
  justify-content: flex-end;
`;
